// components/admin/domain/AddDomainModal.js
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  XMarkIcon,
  GlobeAltIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { validationRules } from '../../../utils/formValidation';
import { sanitizeInput } from '../../../utils/sanitize';

const AddDomainModal = ({ open, onClose, onAdd }) => {
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange' });

  const domainValue = watch('domainName', '');
  const domainPattern = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i;
  const isDomainValid = domainValue.trim() && domainPattern.test(domainValue.trim());

  useEffect(() => {
    if (!open) {
      reset();
      setApiError('');
    }
  }, [open, reset]);

  const onSubmit = async (data) => {
    setApiError('');
    setLoading(true);
    try {
      await onAdd(sanitizeInput(data.domainName.trim()));
      reset();
      onClose();
    } catch (err) {
      setApiError(err.message || 'Failed to add domain');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <GlobeAltIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Add New Domain</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-5 space-y-4">
            {/* Domain Input */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                Domain Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  {...register('domainName', {
                    ...validationRules.domain,
                    validate: {
                      validFormat: (v) =>
                        domainPattern.test(v.trim()) || 'Invalid domain name format',
                      validLength: (v) =>
                        (v.trim().length >= 3 && v.trim().length <= 253) ||
                        'Domain length must be between 3 and 253 characters',
                      validChars: (v) =>
                        /^[a-zA-Z0-9.-]+$/.test(v.trim()) ||
                        'Domain contains invalid characters',
                    },
                  })}
                  disabled={loading}
                  placeholder="example.com"
                  autoFocus
                  className={`w-full px-3 py-2 border rounded-lg text-sm text-gray-900 dark:text-white bg-white dark:bg-[#222222] placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:outline-none transition-colors ${
                    errors.domainName
                      ? 'border-red-300 dark:border-red-600 focus:ring-red-200 dark:focus:ring-red-800'
                      : isDomainValid
                        ? 'border-green-300 dark:border-green-700 focus:ring-green-200 dark:focus:ring-green-800'
                        : 'border-gray-300 dark:border-gray-700 focus:ring-orange-200 dark:focus:ring-orange-800'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                />
                {isDomainValid && !errors.domainName && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <CheckCircleSolid className="w-4 h-4 text-green-500" />
                  </div>
                )}
              </div>

              {/* Error Messages */}
              {(errors.domainName || apiError) && (
                <div className="mt-1.5 space-y-1">
                  {errors.domainName && (
                    <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                      <ExclamationCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{errors.domainName.message}</span>
                    </p>
                  )}
                  {apiError && (
                    <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                      <ExclamationCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{apiError}</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Hint */}
            <p className="text-xs text-gray-400 dark:text-gray-500">
              After adding, you'll verify ownership by adding a DNS record.
            </p>
          </div>

          {/* Footer */}
          <div className="px-5 py-3 bg-gray-50 dark:bg-[#111111] border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#222222] rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !isValid}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Adding...</span>
                </>
              ) : (
                <span>Add Domain</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDomainModal;
